import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Select, { components } from '@atlaskit/select';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { layers } from '@atlaskit/theme/constants';

export enum ConfluenceSpaceType {
	ALL = 'ALL',
	ALL_EXCEPT_PERSONAL = 'ALL_EXCEPT_PERSONAL',
	ALL_EXCEPT_SPECIFIC = 'ALL_EXCEPT_SPECIFIC',
	PERSONAL = 'PERSONAL',
	SPECIFIC = 'SPECIFIC',
}

const i18n = defineMessages({
	ariaLabel: {
		id: 'space-utils.permissions-export.form.space-target-select.aria-label',
		defaultMessage: 'For which spaces',
		description: 'Accessible label for space target select',
	},
	all: {
		id: 'space-utils.space-target-select.all',
		defaultMessage: 'All',
	},
	allDescription: {
		id: 'space-utils.space-target-select.allDescription',
		defaultMessage: 'All spaces, regardless of type',
	},
	allExcludingPersonal: {
		id: 'space-utils.space-target-select.allExcludingPersonal',
		defaultMessage: 'All (excluding personal)',
	},
	allExcludingPersonalDescription: {
		id: 'space-utils.space-target-select.allExcludingPersonalDescription',
		defaultMessage: "Don't include any personal spaces",
	},
	allExcludingSpecific: {
		id: 'space-utils.space-target-select.allExcludingSpecific',
		defaultMessage: 'All (excluding specific spaces)',
	},
	allExcludingSpecificDescription: {
		id: 'space-utils.space-target-select.allExcludingSpecificDescription',
		defaultMessage: 'Choose specific spaces to exclude',
	},
	personal: {
		id: 'space-utils.space-target-select.personal',
		defaultMessage: 'Only personal',
	},
	personalDescription: {
		id: 'space-utils.space-target-select.personalDescription',
		defaultMessage: 'All personal spaces',
	},
	specific: {
		id: 'space-utils.space-target-select.specific',
		defaultMessage: 'Only specific spaces',
	},
	specificDescription: {
		id: 'space-utils.space-target-select.specificDescription',
		defaultMessage: 'Choose specific spaces to include',
	},
});

const menuItemStyle = cssMap({
	style: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
	},
});

const CustomOption = React.memo((props: any) => (
	<components.Option {...props}>
		<Box xcss={menuItemStyle.style}>
			<Text>{props.data.label}</Text>
			<Text color="color.text.subtlest">{props.data.description}</Text>
		</Box>
	</components.Option>
));

const SELECT_STYLE_PROPS = {
	isSearchable: false,
	styles: {
		menu: (base: any) => ({
			...base,
			width: '320px',
			padding: 0,
			margin: 0,
			zIndex: layers.modal() + 1, // Higher than modal to appear above modal content
		}),
		menuPortal: (base: any) => ({
			...base,
			zIndex: layers.modal() + 1, // Higher than modal to appear above modal content
		}),
		control: (base: any) => ({
			...base,
			width: '320px',
			padding: 0,
			margin: 0,
		}),
	},
};

export type SpaceTypeValue =
	| 'ALL'
	| 'ALL_EXCEPT_PERSONAL'
	| 'ALL_EXCEPT_SPECIFIC'
	| 'PERSONAL'
	| 'SPECIFIC';

export interface SpaceTargetOption<T = ConfluenceSpaceType> {
	label: keyof typeof i18n;
	value: T;
	description: keyof typeof i18n;
}

const SPACE_TARGET_OPTIONS: SpaceTargetOption<ConfluenceSpaceType>[] = [
	{
		label: 'all',
		value: ConfluenceSpaceType.ALL,
		description: 'allDescription',
	},
	{
		label: 'allExcludingPersonal',
		value: ConfluenceSpaceType.ALL_EXCEPT_PERSONAL,
		description: 'allExcludingPersonalDescription',
	},
	{
		label: 'allExcludingSpecific',
		value: ConfluenceSpaceType.ALL_EXCEPT_SPECIFIC,
		description: 'allExcludingSpecificDescription',
	},
	{
		label: 'personal',
		value: ConfluenceSpaceType.PERSONAL,
		description: 'personalDescription',
	},
	{
		label: 'specific',
		value: ConfluenceSpaceType.SPECIFIC,
		description: 'specificDescription',
	},
];

/**
 * Generic SpaceTargetSelect component that works with any enum type that has
 * the same string values as ConfluenceSpaceType (ALL, ALL_EXCEPT_PERSONAL, etc.)
 *
 * @template T - The space type enum (defaults to ConfluenceSpaceType for backward compatibility)
 */
export const SpaceTargetSelect = <T extends string = ConfluenceSpaceType>({
	onChange,
	spaceTargetValue,
	menuPortalTarget,
}: {
	onChange: (value: T) => void;
	spaceTargetValue: T;
	menuPortalTarget?: HTMLElement;
}): JSX.Element => {
	const { formatMessage } = useIntl();

	const options = SPACE_TARGET_OPTIONS.map((option) => ({
		label: formatMessage(i18n[option.label]),
		value: option.value as T,
		description: formatMessage(i18n[option.description]),
	}));

	return (
		<Select
			options={options}
			onChange={(newValue) => onChange(newValue?.value as T)}
			value={options.find((option) => String(option.value) === String(spaceTargetValue))}
			components={{ Option: CustomOption }}
			menuPosition="fixed"
			menuPortalTarget={menuPortalTarget}
			aria-label={formatMessage(i18n.ariaLabel)}
			{...SELECT_STYLE_PROPS}
		/>
	);
};
